// ability-preconditions-browser.ts — binds the pure P4 gate (janus-engine/actions/ability-preconditions.ts) to the page's
// caches. Runs at the ONE dispatch chokepoint every browser cast passes (use-ability.ts queryAbilityData — R-025 S1,
// 2026-09-16) and, earlier, at the AoE range click so a refusal lands before placement. Before judging it gathers what a
// rule needs (the owner's Q2 read): the castable payload, the stealth lockout read, the FT3D scene's Hide / Hunker verdicts.
// browserAbilityGateVerdict() answers refuse / allow / unknown; unresolved inputs refuse before any draw.
// A Q2 read that FAILED (an error, a timeout, a stalled scene) refuses fail-closed when the verdict needed its data (owner Q2 = A).
import { abilityGateVerdict, inventoryListEntry, GATE_READ_SITES, type AbilityGateVerdict, type AbilityPreconditionReaders, type SceneGateVerdicts, type StealthLockoutState } from '../janus-engine/actions/ability-preconditions';
import { craftingWaivesNonmagicalReagents } from '../janus-engine/actions/crafting-waiver';
import { resolveAttackComposite, type AttackWeaponReaders } from '../janus-engine/actions/attack-weapon-resolution';
import { castGrantVerdict, type CastGrantVerdict } from '../janus-engine/actions/cast-grant-gate';
import { areaAnchorVerdict, bounceGeometryVerdict, chainGeometryVerdict, sanguineExtraTargets, selectionVisibilityVerdict, targetCountRefusal, targetRuleRefusal, usesCasterInventoryPicker,
    effectiveSpellRangeId, persistentSpellRangeMultiplier, spellRangeMultiplier, throughSummonOrigins,
    type CastGeometryVerdict, type TargetRuleTarget } from '../janus-engine/actions/cast-range-gate';
import { abilityVisibility } from '../janus-engine/ability/visibility';
import { positionKnown, tremorsenseReaches } from '../encounter-map/ft3d/perception';
import { creatureIsInActiveCombat } from '../roll-init/client-combat-membership';
import { pageHurlObjectRefusal, pageTelekineticReach } from '../shared/object-handling-browser';
import { pageCastGrantController, pageControllerRow } from '../shared/summon-cantrips-browser';
import { pageSkillEntries } from '../shared/page-skill-entries';
import { fullDefinition } from '../janus-engine/reactions/php-row-rules';
import { stealthLockoutCalendarRef, stealthLockoutCampaign, stealthLockoutDecision } from '../janus-engine/ability/stealth-lockout';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();

function resolvePageItem(entry: unknown, caster?: Row): Row | null {
    const id = upper(typeof entry === 'string' ? entry : record(entry)?.item_id ?? record(record(entry)?.items)?.item_id);
    if (!id) return null;
    const ft = window.FateTide;
    const held = fullDefinition(record(window.ftFindFullNestedItem?.(id)), 'any')
        ?? fullDefinition(record(ft?.objectCache?.get(id)), 'any');
    if (held) return held;
    // The card finder deliberately skips layerless definitions. Match ability-truth's catalog
    // order without relaxing the finder's shell contract for its other callers.
    const casters = caster ? [caster] : [...(ft?.casterCache?.values() ?? [])].map(record).filter((r): r is Row => !!r);
    for (const root of casters) {
        const encounterId = upper(record(root.encounter_creatures)?.encounter_id);
        const encounter = record(ft?.encounterCache?.get(encounterId) ?? ft?.encounterCache?.get(encounterId.toLowerCase()));
        const roster = Object.values(record(encounter?.encounter_creatures_arr) ?? encounter?.encounter_creatures_arr ?? {});
        for (const catalog of [root.catalog, encounter?.catalog, ...roster.map(member => record(member)?.catalog)]) {
            const def = fullDefinition(record(record(catalog)?.[id]), 'any');
            if (def && upper(ftItemsCol(def, 'item_id')) === id) return def;
        }
    }
    if (!caster) for (const encounter of ft?.encounterCache?.values() ?? []) {
        const def = fullDefinition(record(record(record(encounter)?.catalog)?.[id]), 'any');
        if (def && upper(ftItemsCol(def, 'item_id')) === id) return def;
    }
    // Damage-type picks use the boot registry, whose entries have no item layers.
    return record(Object.values((window as Window & { DAMAGETYPE_CATALOG?: Record<string, { item_id?: string }> }).DAMAGETYPE_CATALOG ?? {})
        .find(type => upper(type?.item_id) === id));
}

const readers: AbilityPreconditionReaders = {
    value: (row, key) => row ? ftLayerValue(row, key) : null,
    has: (row, key) => !!row && (ftLayerValue(row, key) != null || (ftLayerEntries(row, key) ?? []).length > 0),
    // creature_statuses() presents each definition under status_arr; cached rows may carry only the catalog ref.
    statuses: row => (ftCreatureStatuses(row, 'target') ?? []).flatMap((entry: unknown) => {
        const status = record(entry);
        if (!status) return [];
        if (record(status.status_arr)) return [status];
        const ref = upper(status.status_item_id ?? status.status_arr);
        const definition = ref ? record(window.FateTide?.objectCache?.get(ref)) : null;
        return [definition ? { ...status, status_arr: definition } : status];
    }),
    inCombat: (encounter, caster) => creatureIsInActiveCombat(encounter, caster as never),
    entries: (row, key) => row ? (ftLayerEntries(row, key) ?? []) : [],
    inventoryField: (row, key) => row ? ftInventoryField(row, key) : null,
    // ftLayerValue carries the creature size bridge (final_size_id); an instance size override lives in inventory_size.
    creatureSizeId: creature => ftLayerValue(creature, 'size'),
    itemSizeId: row => ftInventoryField(row, 'inventory_size') || ftLayerValue(row, 'size'),
    // DOS2-style telekinesis (2026-10-07): object_interaction_range_ft + whether the floor row lies within it.
    telekineticReach: (creature, row) => pageTelekineticReach(creature, row),
    // Hurl mentas (2026-10-08): a picked, still-eligible unattended floor object (object-handling-browser.ts).
    hurlObjectRefusal: (creature, ability) => pageHurlObjectRefusal(creature, ability),
    // The thrown row: the page's cached copy of any inventory instance (casters' trees, encounter floors, the object cache).
    resolveInventory: id => {
        try {
            return record((window as Window & { ftResolveCachedInventoryRow?: (id: string) => unknown }).ftResolveCachedInventoryRow?.(id));
        } catch {
            return null;
        }
    },
    resolveItem: entry => {
        try { return resolvePageItem(entry); }
        catch { return null; }
    },
    // a feat's requires_skill_tier_N gate (Spell Sniper → Far Bender) is judged against this registry (cast-range-gate casterLayerProvider)
    get skillEntries() { return pageSkillEntries(); },
};
/** get_weapon_for_attack_ability()'s pinned-weapon path over the same page readers (ftLayerValue bridges the equipped counts). */
const weaponReaders: AttackWeaponReaders = {
    value: readers.value,
    entries: (row, key) => row ? (ftLayerEntries(row, key) ?? []) : [],
    inventoryField: (row, key) => row ? ftInventoryField(row, key) : null,
    itemType: row => {
        try {
            return String(ftItemsCol(row, 'item_type') ?? '').toLowerCase();
        } catch {
            return String(record(row.items)?.item_type ?? '').toLowerCase();
        }
    },
};

/** Hide / Hunker Down — the two abilities whose refusals need the FT3D scene (S4b). */
const HIDE_ABILITY = 'C7326C89-CD32-C8C8-2DA9-29D66FFC2284';
const HUNKER_ABILITY = '8C28EBE3-60EA-D493-0FB8-67859CE92F63';
/** How long the chokepoint waits for the scene's verdict: one postMessage round trip, which the frame answers between render
 *  frames (typically < 20 ms; the budget covers a busy WebGL loop on a slow machine). A mounted scene that does not answer in
 *  time is a FAILED read (fail-closed); no mounted scene is simply no data (the cast keeps the PHP gate). */
const SCENE_VERDICT_TIMEOUT_MS = 1000;
/** The fail-closed refusal (owner Q2 = A): a read the verdict needed failed, so the page cannot vouch for the cast. */
export const GATE_READ_FAILED_MESSAGE = 'Could not verify that action right now — please try again.';

type GateRead = 'castable' | 'lockout' | 'scene' | 'names';
/** Per-cast data the Q2 read gathered for the gate: the stealth lockout ledger read, the scene verdicts, and which reads FAILED.
 *  Returned by ensureAbilityGateData() to the dispatch that asked, which hands it straight to browserAbilityGateVerdict() —
 *  bound to that one cast (caster + ability + encounter), never shared through page state (Codex review 2026-09-16). */
export interface GatheredGateData {
    sceneVerdicts?: SceneGateVerdicts | null; stealthLockout?: StealthLockoutState | null;
    /** Status names the page's catalog lacked, read from get_item_id_name() (gate_status_names_inc.php, R-025 S6). */
    names?: Record<string, string>;
    failedReads: GateRead[];
    casterId: string; abilityId: string; encounterId: string;
}
/** The lockout / castable reads' deadline: a stalled endpoint must never hold a cast (the read FAILS and a cast it matters to is
 *  refused). */
const LOCKOUT_READ_TIMEOUT_MS = 3000;
const NAMES_READ_TIMEOUT_MS = 3000;
/** Names already read this page load (a status name is catalog data: stable for the session). */
const statusNameCache = new Map<string, string>();
/** The status requirement ids an ability names whose name the page's catalog cannot supply. */
function unnamedStatusIds(ability: Row | null): string[] {
    if (!ability) return [];
    const ids = new Set<string>();
    for (const layer of ['if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active']) {
        for (const entry of ftLayerEntries(ability, layer) ?? []) {
            const row = record(entry);
            const id = upper(typeof entry === 'string' ? entry.split('|')[0] : row?.item_layer_effect ?? record(row?.items)?.item_id ?? row?.item_id);
            if (!/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id) || statusNameCache.has(id)) continue;
            const definition = readers.resolveItem?.(id) ?? null;
            const name = ftItemsCol(definition, 'item_name') || ftItemsCol(entry, 'item_name');
            if (typeof name !== 'string' || name === '') ids.add(id);
        }
    }
    return [...ids];
}
/** get_item_id_name() for the ids the gate needs; failed on any error, error payload or the deadline. */
async function fetchStatusNames(ids: string[]): Promise<boolean> {
    const abort = new AbortController();
    const deadline = window.setTimeout(() => abort.abort(), NAMES_READ_TIMEOUT_MS);
    try {
        const response = await fetch(`${BASE_URL}options/api/gate_status_names_inc.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: ids.slice(0, 32) }), signal: abort.signal,
        });
        const payload = record(await response.json());
        const names = record(payload?.names);
        if (!response.ok || !payload || payload.error || !names) return false;
        for (const [id, name] of Object.entries(names)) if (typeof name === 'string' && name !== '') statusNameCache.set(upper(id), name);
        return true;
    } catch (error) {
        console.error('Status name read failed; a status-gated cast that needs it is refused', error);
        return false;
    } finally {
        window.clearTimeout(deadline);
    }
}

/** Warm the definitions this cast needs through the page's existing object reader. Ordinary
 * casts ask the gate which missing name it actually reached; crafting also needs output data. */
export async function ensureAbilityRequirementDefinitions(ability: Row, gate?: BrowserGateRequest): Promise<void> {
    if (gate && !['requires_tool', 'requires_reagent', 'requires_equipped_item', 'consumes_ability_charges', 'add_charges_to_ability',
        'ability_active_if_ability_charge_is_at_median', 'if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active']
        .some(key => readers.has(ability, key))) return;
    const fetchObject = (window as Window & { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
    if (typeof fetchObject !== 'function') return;
    const ids = new Set<string>();
    if (gate) {
        const verdict = browserAbilityGateVerdict(gate, ids);
        if (verdict.kind !== 'unknown' || !verdict.site.endsWith('-name')) return;
    } else {
        for (const key of ['requires_reagent', 'requires_tool', 'fabricate']) {
            for (const entry of readers.entries?.(ability, key) ?? []) {
                const id = upper(inventoryListEntry(entry).uuid);
                if (/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/.test(id) && !readers.resolveItem?.(id)) ids.add(id);
            }
        }
    }
    const pending = [...ids];
    for (let at = 0; at < pending.length; at += 32) {
        await Promise.allSettled(pending.slice(at, at + 32).map(id => fetchObject(id)));
    }
}

const sceneFrame = (): HTMLIFrameElement | null => document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
let sceneRequestSequence = 0;
const pendingSceneRequests = new Map<string, (verdicts: SceneGateVerdicts | null) => void>();
let sceneListenerBound = false;
function bindSceneListener(): void {
    if (sceneListenerBound) return;
    sceneListenerBound = true;
    window.addEventListener('message', event => {
        if (event.origin !== window.location.origin) return;
        const envelope = event.data as { type?: string; payload?: unknown } | null;
        if (envelope?.type !== 'ft3d-campaign-gate-query-response-v1') return;
        const frame = sceneFrame();
        if (!frame || event.source !== frame.contentWindow) return;
        const payload = record(envelope.payload);
        const requestId = String(payload?.requestId ?? '');
        const resolve = pendingSceneRequests.get(requestId);
        if (!resolve) return;
        pendingSceneRequests.delete(requestId);
        const verdict = (value: unknown): boolean | null => (value === true || value === false ? value : null);
        const hide = record(payload?.hide);
        const hunker = record(payload?.hunker);
        resolve({
            hide: hide ? { zoneOpen: verdict(hide.zoneOpen), seenClearly: verdict(hide.seenClearly) } : null,
            hunker: hunker ? { aloft: verdict(hunker.aloft), noCover: verdict(hunker.noCover) } : null,
            lineOfEffect: payload?.lineOfEffect === 'clear' || payload?.lineOfEffect === 'blocked' ? payload.lineOfEffect : null,
            burrowedAnchor: payload?.burrowedAnchor === 'clear' || payload?.burrowedAnchor === 'blocked' ? payload.burrowedAnchor : null,
        });
    });
}
/** Ask the mounted FT3D scene for its Hide / Hunker verdicts (ft3d-campaign-gate-query-request-v1 → -response-v1). `failed` when a
 *  mounted scene did not answer in time (or the post threw); verdicts null without failure when no scene is mounted. */
function requestSceneVerdicts(request: { casterId: string; encounterId: string; abilityId: string; lineOfEffectTo?: string; burrowedAnchorTo?: string }): Promise<{ verdicts: SceneGateVerdicts | null; failed: boolean }> {
    const frame = sceneFrame();
    const target = frame?.contentWindow ?? null;
    if (!frame || !target) return Promise.resolve({ verdicts: null, failed: false });
    bindSceneListener();
    sceneRequestSequence += 1;
    const requestId = `gate-query-${sceneRequestSequence}-${Date.now()}`;
    return new Promise(resolve => {
        const timer = window.setTimeout(() => { pendingSceneRequests.delete(requestId); resolve({ verdicts: null, failed: true }); }, SCENE_VERDICT_TIMEOUT_MS);
        pendingSceneRequests.set(requestId, verdicts => { window.clearTimeout(timer); resolve({ verdicts, failed: false }); });
        try {
            target.postMessage({ type: 'ft3d-campaign-gate-query-request-v1', payload: { schema: 'ft.ft3d.gate-query.v1', requestId,
                casterId: request.casterId, encounterId: request.encounterId, abilityId: request.abilityId,
                ...(request.lineOfEffectTo ? { lineOfEffectTo: request.lineOfEffectTo } : {}),
                ...(request.burrowedAnchorTo ? { burrowedAnchorTo: request.burrowedAnchorTo } : {}) } }, window.location.origin);
        } catch {
            pendingSceneRequests.delete(requestId); window.clearTimeout(timer); resolve({ verdicts: null, failed: true });
        }
    });
}
/** Only the small raw clock/calendar read is retained; ledger deadlines are read for every cast. */
const stealthCampaignFacts = new Map<string, Row>();
/** Read raw facts, then judge natively. Reuse a held calendar only when the saved identity/revision still matches. */
async function fetchStealthLockout(casterId: string, encounterId: string): Promise<{ state: StealthLockoutState | null; failed: boolean }> {
    const abort = new AbortController();
    const deadline = window.setTimeout(() => abort.abort(), LOCKOUT_READ_TIMEOUT_MS);
    try {
        const caster = record(window.FateTide?.casterCache?.get(upper(casterId)));
        const encounter = record(window.FateTide?.encounterCache?.get(upper(encounterId)));
        const pageCampaign = record((window.FateTide as { campaignCache?: Map<string, unknown> } | undefined)
            ?.campaignCache?.get(upper(window.ftGetCampaignId?.())));
        const held = [record(encounter?.campaign_arr), record(caster?.campaign_arr), pageCampaign, stealthCampaignFacts.get(upper(casterId))]
            .find(campaign => stealthLockoutCalendarRef(campaign));
        const response = await fetch(`${BASE_URL}options/api/stealth_lockout_inc.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ caster_id: casterId, held_calendar: stealthLockoutCalendarRef(held) }), signal: abort.signal,
        });
        const payload = record(await response.json());
        const state = response.ok ? stealthLockoutDecision(payload, held) : null;
        if (!state) return { state: null, failed: true };
        const campaign = payload && stealthLockoutCampaign(payload, held);
        if (campaign) stealthCampaignFacts.set(upper(casterId), campaign);
        return { state, failed: false };
    } catch (error) {
        console.error('Stealth lockout read failed or timed out; a Hide that needs it is refused', error);
        return { state: null, failed: true };
    } finally {
        window.clearTimeout(deadline);
    }
}

/** The owner's Q2 read (2026-09-16): when a rule's data is not on the page, fetch it into the cache BEFORE judging.
 *  - the innate per-long-rest pool — a compiled row carries no charges cluster for a spell that declares no
 *    ability_charges, but the pre-baked castable payload (computed.creature.castable) carries the pool's charges; a cold
 *    compile lacks the bake, so the sheet's own fallback endpoint fills it;
 *  - Hide (S4b): the stealth lockout ledger + campaign clock (read-only endpoint) and the scene's watcher verdicts;
 *  - Hunker Down (S4b): the scene's aloft / no-cover verdicts.
 *  Resolves when the cache is as complete as it can be; every read that FAILED is listed in failedReads (S5 fail-closed). */
export async function ensureAbilityGateData(request: { casterId: string; variable1?: unknown; variable2?: unknown; abilityId?: unknown; encounterId?: unknown }): Promise<GatheredGateData> {
    const abilityId = upper(request.abilityId);
    const encounterId = String(request.encounterId ?? '');
    const perCast: GatheredGateData = { failedReads: [], casterId: upper(request.casterId), abilityId, encounterId: upper(encounterId) };
    if (abilityId === HIDE_ABILITY || abilityId === HUNKER_ABILITY) {
        const [scene, lockout] = await Promise.all([
            requestSceneVerdicts({ casterId: request.casterId, encounterId, abilityId }),
            abilityId === HIDE_ABILITY ? fetchStealthLockout(request.casterId, encounterId) : Promise.resolve({ state: null, failed: false }),
        ]);
        perCast.sceneVerdicts = scene.verdicts;
        perCast.stealthLockout = lockout.state;
        if (scene.failed) perCast.failedReads.push('scene');
        if (lockout.failed) perCast.failedReads.push('lockout');
    }
    // R-025 S6: the status names a required / forbidden status refusal prints, when the page's catalog lacks them.
    let gatedAbility: Row | null = null;
    try { gatedAbility = record(window.ftFindFullNestedItem?.(abilityId)) ?? record(window.FateTide?.objectCache?.get(abilityId)); } catch { gatedAbility = null; }
    const unnamed = unnamedStatusIds(gatedAbility);
    // The endpoint takes 32 ids per read: batch, so no requirement is silently dropped (Codex review).
    for (let at = 0; at < unnamed.length; at += 32) {
        if (!(await fetchStatusNames(unnamed.slice(at, at + 32)))) { perCast.failedReads.push('names'); break; }
    }
    if (String(request.variable1 ?? '') === 'innate_spell_per_long_rest') {
        const key = upper(request.casterId);
        const cachedCaster = (): Row | null => { const casters = window.FateTide?.casterCache; return record(casters?.get(request.casterId) ?? casters?.get(key)); };
        const caster = cachedCaster();
        const derive = (window as unknown as { ftCreatureCastablePayload?: (row: Row) => Row }).ftCreatureCastablePayload;
        try {
            if (!caster || !derive) throw new Error('unported:castable:browser-inputs-not-held');
            const computed = record(caster.computed) ?? {};
            const creature = record(computed.creature) ?? {};
            caster.computed = { ...computed, creature: { ...creature, castable: derive(caster) } };
        } catch { perCast.failedReads.push('castable'); }
    }
    // Use the settled per-cast reads, just as the dispatch does, and install missing names
    // through fetchAndCacheObject into the object cache the engine's readers consult too.
    if (gatedAbility) await ensureAbilityRequirementDefinitions(gatedAbility, { casterId: request.casterId, encounterId,
        ability: gatedAbility, variable1: request.variable1, variable2: request.variable2, gathered: perCast });
    return perCast;
}

export interface BrowserGateRequest {
    casterId: string; encounterId: string; ability: unknown; variable1?: unknown; variable2?: unknown;
    /** The Quick Ritual opt-in when the dispatcher has already consumed the one-shot window flag into its payload. */
    quickRitual?: boolean;
    /** The weapon fold-out's use_weapon_inventory_id and the action bar's consume_inventory_id (absorbed-gear gate, composite). */
    weaponInventoryId?: unknown; consumeInventoryId?: unknown;
    consumeInventoryUnit?: true;
    /** What ensureAbilityGateData() returned for THIS cast: used only when it names the same caster, ability and encounter — data
     *  gathered for another cast never applies. */
    gathered?: GatheredGateData | null;
}

/**
 * The CAST-LEGITIMACY check (janus-engine/actions/cast-grant-gate.ts — owner 2026-10-08, "browser-side check only"): refuses a cast the page
 * PROVES is not the caster's (an ability it holds through no grant — e.g. an ability_choice branch cast without its parent). Anything the
 * check cannot classify is ALLOWED and logged at debug level (console.debug), never refused. Runs inside browserAbilityGateVerdict (every
 * dispatch that passes the precondition gate) and, for the instant-volley lane that skips it, from use-ability.ts directly.
 */
/** One dispatch asks more than once (useAbility's early check, then the lane's gate): an ALLOW is memoized per caster ROW object (a
 *  caster_update replaces the row) and per ability / variables / encounter row. A stale allow is only an allow; a refusal is always fresh. */
const castGrantMemo = new WeakMap<object, Map<string, { encounter: Row | null; ability: Row; controller: Row | null | undefined; verdict: CastGrantVerdict }>>();
export function browserCastGrantVerdict(request: { casterId: string; ability: unknown; variable1?: unknown; variable2?: unknown; weaponInventoryId?: unknown;
    consumeInventoryId?: unknown; encounterId?: unknown }): CastGrantVerdict {
    const casters = window.FateTide?.casterCache;
    const caster = record(casters?.get(request.casterId) ?? casters?.get(upper(request.casterId)));
    const ability = record(request.ability);
    const abilityId = upper(record(ability?.items)?.item_id ?? ability?.item_id);
    let verdict: CastGrantVerdict;
    if (!caster || !ability) verdict = { kind: 'unclassified', reason: caster ? 'ability-row' : 'caster-row' };
    else {
        try {
            const catalog = record(caster.catalog);
            // the caster's encounter (its scene objects can carry castable abilities — a light source's own)
            const encounterId = upper(request.encounterId) || upper(record(caster.encounter_creatures)?.encounter_id);
            const encounters = window.FateTide?.encounterCache;
            const encounter = encounterId ? record(encounters?.get(encounterId) ?? encounters?.get(encounterId.toLowerCase())) : null;
            const memoKey = JSON.stringify([abilityId, request.variable1 ?? null, upper(request.weaponInventoryId), upper(request.consumeInventoryId)]);
            const memo = castGrantMemo.get(caster)?.get(memoKey);
            // summons_gain_caster_cantrips: a summon's verdict also depends on its controller row (a recompile replaces it)
            const controller = pageCastGrantController(caster);
            if (memo && memo.encounter === encounter && memo.ability === ability && memo.controller === controller) return memo.verdict;
            verdict = castGrantVerdict({ caster, ability, variable1: request.variable1, weaponInventoryId: request.weaponInventoryId,
                consumeInventoryId: request.consumeInventoryId, encounter,
                // summons_gain_caster_cantrips: a summon's controller (its cantrips are the summon's too) — summon-cantrips-browser.ts
                controller }, {
                value: readers.value, statuses: readers.statuses, entries: (row, key) => row ? (ftLayerEntries(row, key) ?? []) : [],
                // the caster's own catalog first (a map read), then every cache the page holds
                resolveItem: entry => {
                    const id = upper(typeof entry === 'string' ? entry.split('|')[0] : record(entry)?.item_id ?? record(record(entry)?.items)?.item_id);
                    const own = id ? record(catalog?.[id]) : null;
                    return own && record(own.item_layer_types) ? own : (readers.resolveItem ? readers.resolveItem(id) : null);
                },
            });
            // a REFUSAL is never memoized: a row patched in place (a grant painted onto it) must be judged afresh — only allows are reused
            if (verdict.kind !== 'refuse') {
                const perCaster = castGrantMemo.get(caster) ?? new Map();
                if (perCaster.size > 64) perCaster.clear();
                perCaster.set(memoKey, { encounter, ability, controller, verdict });
                castGrantMemo.set(caster, perCaster);
            }
        } catch (error) {
            verdict = { kind: 'unclassified', reason: 'gate-error' };
            console.debug('[cast-grant] check failed; the cast is allowed', error);
        }
    }
    if (verdict.kind !== 'granted') console.debug(`[cast-grant] ${verdict.kind === 'refuse' ? 'REFUSED' : 'allowed (unclassified: ' + verdict.reason + ')'}`,
        { casterId: upper(request.casterId), abilityId, variable1: request.variable1 });
    return verdict;
}

/** R-025 S5 — the authoritative verdict for one browser cast. */
export function browserAbilityGateVerdict(request: BrowserGateRequest, missingNames?: Set<string>): AbilityGateVerdict {
    const casters = window.FateTide?.casterCache;
    const caster = record(casters?.get(request.casterId) ?? casters?.get(upper(request.casterId)));
    const ability = record(request.ability);
    if (!caster) return { kind: 'unknown', site: 'caster-row' };
    if (!ability) return { kind: 'unknown', site: 'ability-row' };
    // The cast-legitimacy check first: a cast of an ability the caster does not have is refused before any other rule speaks.
    const grant = browserCastGrantVerdict({ casterId: request.casterId, ability, variable1: request.variable1,
        weaponInventoryId: request.weaponInventoryId, consumeInventoryId: request.consumeInventoryId, encounterId: request.encounterId });
    if (grant.kind === 'refuse') return { kind: 'refuse', message: grant.message };
    const encounters = window.FateTide?.encounterCache;
    const encounter = request.encounterId
        ? record(encounters?.get(request.encounterId) ?? encounters?.get(upper(request.encounterId)))
        : null;
    const abilityId = upper(ability.items && typeof ability.items === 'object' ? (ability.items as Row).item_id : ability.item_id);
    const perCast = request.gathered && request.gathered.casterId === upper(request.casterId) && request.gathered.abilityId === abilityId
        && request.gathered.encounterId === upper(request.encounterId) ? request.gathered : null;
    try {
        // An attack is judged on the composite janus builds from the same pin (or left to PHP when the page cannot build it).
        const resolution = resolveAttackComposite({ ability, caster, weaponInventoryId: request.weaponInventoryId }, {
            ...weaponReaders,
            // A natural weapon janus picks is query_full_item_arr(id): the page's catalog definition.
            resolveItem: id => readers.resolveItem ? readers.resolveItem(id) : null,
            // The head-attack gate needs the caster's combat state, provable only from its own encounter row.
            inCombat: row => encounter && upper(record(encounter.encounters)?.encounter_id ?? encounter.encounter_id) === upper(record(row.encounter_creatures)?.encounter_id)
                ? creatureIsInActiveCombat(encounter, row as never) : null,
        });
        if (resolution && 'unknown' in resolution) return { kind: 'unknown', site: resolution.unknown };
        const verdict = abilityGateVerdict({
            caster, actionCaster: pageControllerRow(caster), ability: resolution?.composite ?? ability, encounter, variable1: request.variable1, variable2: request.variable2,
            sceneVerdicts: perCast?.sceneVerdicts ?? null, stealthLockout: perCast?.stealthLockout ?? null,
            // The dispatcher's payload is authoritative (it already consumed the one-shot flag); the click-time callers
            // pass nothing and read the armed flag.
            quickRitual: request.quickRitual ?? ((window as { ftQuickRitualNextCast?: boolean }).ftQuickRitualNextCast === true),
            weaponInventoryId: request.weaponInventoryId, consumeInventoryId: request.consumeInventoryId,
            consumeInventoryUnit: request.consumeInventoryUnit,
        }, { ...readers, name: id => {
            const name = statusNameCache.get(upper(id)) ?? null;
            if (name === null) missingNames?.add(upper(id));
            return name;
        } });
        // Owner Q2 = A: the verdict needed a per-cast read and that read FAILED — refuse rather than guess (fail-closed).
        const read = verdict.kind === 'unknown' ? GATE_READ_SITES[verdict.site] : undefined;
        if (read && perCast?.failedReads.includes(read)) return { kind: 'refuse', message: GATE_READ_FAILED_MESSAGE };
        return verdict;
    } catch (error) {
        console.error('Ability precondition gate failed; the cast keeps the server gate', error);
        return { kind: 'unknown', site: 'gate-error' };
    }
}

/** crafting_waives_nonmagical_reagents (Wizard v9 Universal Crafter) on the cached crafter row: true / false; null = no row, or a provider
 *  the page cannot resolve. The craft start lane and the PC sheet's Crafting tab read it (window.ftCraftingWaiverHeld). */
export function browserCraftingWaiverHeld(casterId: string): boolean | null {
    const casters = window.FateTide?.casterCache;
    const caster = record(casters?.get(casterId) ?? casters?.get(upper(casterId)));
    if (!caster) return null;
    try {
        return craftingWaivesNonmagicalReagents(readers, caster);
    } catch (error) {
        console.error('Crafting waiver read failed', error);
        return null;
    }
}
(window as unknown as { ftCraftingWaiverHeld?: (casterId: string) => boolean | null }).ftCraftingWaiverHeld = browserCraftingWaiverHeld;

/** PHP's refusal message when the page proves a refusal (or a needed read failed); null otherwise. The AoE range click uses it. */
export function browserAbilityPreconditionRefusal(request: BrowserGateRequest): string | null {
    const verdict = browserAbilityGateVerdict(request);
    return verdict.kind === 'refuse' ? verdict.message : null;
}

/** A roster member of the cached encounter by encounter_creature_id (the row carrying the live position). */
function rosterMember(encounter: Row | null, id: string): Row | null {
    const roster = Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as unknown[] : [];
    for (const entry of roster) {
        const member = record(entry);
        if (member && upper(record(member.encounter_creatures)?.encounter_creature_id) === upper(id)) return member;
    }
    return null;
}

/** Cast-gate session (2026-09-29): the Bouncing Spell geometry (validate_and_adjust_targets' Bouncing block) over the page's rows.
 *  The caster is its full cached row carrying the roster member's live position; the targets are the roster members. */
export function browserBounceVerdict(request: { casterId: string; encounterId: string; ability: unknown; targetId: unknown; variable1?: unknown }): CastGeometryVerdict {
    const casters = window.FateTide?.casterCache;
    const cached = record(casters?.get(request.casterId) ?? casters?.get(upper(request.casterId)));
    const ability = record(request.ability);
    const encounters = window.FateTide?.encounterCache;
    const encounter = request.encounterId ? record(encounters?.get(request.encounterId) ?? encounters?.get(upper(request.encounterId))) : null;
    const tokens = String(request.targetId ?? '').split(',').map(token => token.trim()).filter(Boolean);
    if (tokens.length !== 2) return { kind: 'not-bounce' };
    if (!cached || !ability || !encounter) return { kind: 'unknown', site: 'bounce-page-rows' };
    const member = rosterMember(encounter, request.casterId);
    const caster = member ? { ...cached, position: member.position ?? cached.position } : cached;
    try {
        const roster = Array.isArray(encounter.encounter_creatures_arr) ? (encounter.encounter_creatures_arr as unknown[]).map(record).filter((r): r is Record<string, unknown> => r !== null) : [];
        return bounceGeometryVerdict({ caster, ability, tokens, targets: tokens.map(token => rosterMember(encounter, token)), variable1: request.variable1, roster },
            { ...readers, visibility: (viewer, target) => abilityVisibility(encounter, viewer, target) });
    } catch (error) {
        console.error('Bouncing geometry gate failed; the cast posts unjudged', error);
        return { kind: 'unknown', site: 'bounce-gate-error' };
    }
}

/** The CHAIN geometry (chain_targets_max / chain_distance_ft, 2026-10-07 — R-175): each pick after the first within chain_distance_ft of the
 *  primary, over the cached roster rows. 'not-bounce' = nothing to judge (no chain host, or a single pick). */
export function browserChainVerdict(request: { casterId: string; encounterId: string; ability: unknown; targetId: unknown }): CastGeometryVerdict {
    const ability = record(request.ability);
    const tokens = String(request.targetId ?? '').split(',').map(token => token.trim()).filter(Boolean);
    if (!ability || tokens.length < 2) return { kind: 'not-bounce' };
    const encounters = window.FateTide?.encounterCache;
    const encounter = request.encounterId ? record(encounters?.get(request.encounterId) ?? encounters?.get(upper(request.encounterId))) : null;
    const casters = window.FateTide?.casterCache;
    const caster = record(casters?.get(request.casterId) ?? casters?.get(upper(request.casterId))) ?? {};
    try {
        if (!encounter) return chainGeometryVerdict({ caster, ability, tokens, targets: tokens.map(() => null) }, { ...readers, visibility: () => null });
        return chainGeometryVerdict({ caster, ability, tokens, targets: tokens.map(token => rosterMember(encounter, token)) },
            { ...readers, visibility: (viewer, target) => abilityVisibility(encounter, viewer, target) });
    } catch (error) {
        console.error('Chain geometry gate failed; the cast posts unjudged', error);
        return { kind: 'unknown', site: 'chain-gate-error' };
    }
}

/**
 * A player's direct SELECTION, judged before it posts (cast-gate session 2026-09-29, R-103 — PHP no longer re-judges it): creatures the
 * caster cannot see are dropped (all dropped → PHP's refusal), then the target-count ceiling. Returns the selection to post (the dropped
 * ids removed) and the names dropped, or PHP's refusal message. A map-point (area) selection is the FT3D stage's and passes untouched.
 */
export function browserSelectionVerdict(request: { casterId: string; encounterId: string; ability: unknown; targetId: unknown; variable1?: unknown;
  inCampaign: boolean; sightOnly?: boolean }): { kind: 'refuse'; message: string } | { kind: 'post'; targetId: string; dropped: string[] } {
    const raw = String(request.targetId ?? '');
    const tokens = raw.split(',').map(token => token.trim()).filter(Boolean);
    const casters = window.FateTide?.casterCache;
    const cached = record(casters?.get(request.casterId) ?? casters?.get(upper(request.casterId)));
    const ability = record(request.ability);
    const encounters = window.FateTide?.encounterCache;
    const encounter = request.encounterId ? record(encounters?.get(request.encounterId) ?? encounters?.get(upper(request.encounterId))) : null;
    if (!tokens.length || !cached || !ability || tokens.some(token => token.includes('|'))) return { kind: 'post', targetId: raw, dropped: [] };
    const member = encounter ? rosterMember(encounter, request.casterId) : null;
    const caster = member ? { ...cached, position: member.position ?? cached.position } : cached;
    try {
        let kept = tokens;
        let dropped: string[] = [];
        if (encounter) {
            const sight = selectionVisibilityVerdict({ caster, ability, targets: tokens.map(id => ({ id, row: rosterMember(encounter, id) })) },
                { ...readers, visibility: (viewer, target) => abilityVisibility(encounter, viewer, target) });
            if (sight.kind === 'refuse') return sight;
            kept = tokens.filter(id => !sight.drop.includes(upper(id)));
            dropped = sight.names;
        }
        if (request.sightOnly) return { kind: 'post', targetId: kept.length === tokens.length ? raw : kept.join(','), dropped };
        const refusal = targetCountRefusal({ caster, ability, count: kept.length, variable1: request.variable1, inCampaign: request.inCampaign,
            mapPoint: false, inventoryPicker: usesCasterInventoryPicker(ability, readers) }, readers);
        if (refusal) return { kind: 'refuse', message: refusal };
        return { kind: 'post', targetId: kept.length === tokens.length ? raw : kept.join(','), dropped };
    } catch (error) {
        console.error('Selection gate failed; the cast posts as selected', error);
        return { kind: 'post', targetId: raw, dropped: [] };
    }
}

/** The entry for an inventory id in this row's carried tree (containers recursed), or null. (caster_carries_inventory_target) */
function carriedEntry(row: Row | null, inventoryId: string): Row | null {
    let found = null as Row | null;
    const walk = (list: unknown, depth: number): boolean => Array.isArray(list) && depth < 8 && list.some(entry => {
        const item = record(entry);
        if (!item) return false;
        if (upper(ftInventoryField(item, 'inventory_id') ?? record(item.inventory)?.inventory_id ?? item.inventory_id) === inventoryId) { found = item; return true; }
        return walk(item.inventory_in_container ?? item.container_contents, depth + 1);
    });
    if (!row) return null;
    // The compiled row serves the carried tree as the `inventory` layer (charge-adjust's findInventoryRow); older rows carry it bare.
    const layered = ftLayerEntries(row, 'inventory') ?? [];
    if (!walk(layered, 0) && Array.isArray(row.inventory) && row.inventory !== layered) walk(row.inventory, 0);
    return found;
}
const carries = (row: Row | null, inventoryId: string): boolean => carriedEntry(row, inventoryId) !== null;
const UUID_TOKEN = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/** item_field($row, 'item_type') — null when the page cannot read it. */
function rowItemType(row: Row): string | null {
    let type: unknown;
    try { type = ftItemsCol(row, 'item_type'); } catch { type = record(row.items)?.item_type; }
    const text = String(type ?? '').trim();
    return text === '' ? null : text;
}
function rowName(row: Row, fallback: string): string {
    let itemName: unknown = null;
    try { itemName = ftItemsCol(row, 'item_name'); } catch { itemName = record(row.items)?.item_name; }
    return String(record(row.player_characters)?.player_name ?? record(row.encounter_creatures)?.encounter_creature_name ?? itemName ?? fallback);
}
/** The page's rows for one cast: the cached caster carrying its roster member's live position, and the cached encounter. */
function castRows(casterId: string, encounterId: string): { caster: Row | null; member: Row | null; encounter: Row | null } {
    const casters = window.FateTide?.casterCache;
    const cached = record(casters?.get(casterId) ?? casters?.get(upper(casterId)));
    const encounters = window.FateTide?.encounterCache;
    const encounter = encounterId ? record(encounters?.get(encounterId) ?? encounters?.get(upper(encounterId))) : null;
    const member = encounter ? rosterMember(encounter, casterId) : null;
    return { caster: cached && member ? { ...cached, position: member.position ?? cached.position } : cached, member, encounter };
}

/**
 * The per-target RULE gates for a player's selection (cast-gate session 2026-09-29, R-103 — deleted from PHP for a player cast): the
 * item-type list, reach into another creature's stowed gear, and both hands free to pilot a war machine. PHP's refusal text, or null.
 * A map-point selection (an area's aim) is the stage's; the only area carrying an item-type list (Spot Hidden Creatures) is exempt.
 */
export function browserTargetRuleRefusal(request: { casterId: string; encounterId: string; ability: unknown; targetId: unknown }): string | null {
    const ability = record(request.ability);
    const tokens = String(request.targetId ?? '').split(',').map(token => token.trim().toUpperCase()).filter(Boolean);
    if (!ability || !tokens.length || tokens.some(token => !UUID_TOKEN.test(token))) return null;
    const { caster, member, encounter } = castRows(request.casterId, request.encounterId);
    if (!caster) return null;
    const roster = Array.isArray(encounter?.encounter_creatures_arr) ? (encounter!.encounter_creatures_arr as unknown[]).map(record).filter((row): row is Row => !!row) : [];
    const floor = Array.isArray(encounter?.encounter_inventories_arr) ? (encounter!.encounter_inventories_arr as unknown[]).map(record).filter((row): row is Row => !!row) : [];
    try {
        const targets: TargetRuleTarget[] = tokens.map(id => {
            const creature = encounter ? rosterMember(encounter, id) : (id === upper(request.casterId) ? caster : null);
            if (creature) return { row: creature, itemType: rowItemType(creature), name: rowName(creature, 'this target') };
            const own = carriedEntry(caster, id) ?? carriedEntry(member, id);
            if (own) return { row: own, itemType: rowItemType(own), name: rowName(own, 'this target'), carriedByCaster: true };
            for (const holder of roster) {
                const held = carriedEntry(holder, id);
                if (held) return { row: held, itemType: rowItemType(held), name: rowName(held, 'this target'), holder: { name: rowName(holder, 'that creature') },
                    equipped: String(ftInventoryField(held, 'equipped') ?? '') !== '' };
            }
            const loose = floor.find(row => upper(ftInventoryField(row, 'inventory_id')) === id) ?? readers.resolveInventory?.(id) ?? null;
            return { row: loose, itemType: loose ? rowItemType(loose) : null, name: loose ? rowName(loose, 'this target') : 'this target', holder: null };
        });
        return targetRuleRefusal({ caster, ability, targets }, readers);
    } catch (error) {
        console.error('Target rule gate failed; the cast posts unjudged', error);
        return null;
    }
}

/**
 * A creature-anchored AREA (a follow aura, no map point) — the anchor block validate_and_adjust_targets ran for a player (R-103, deleted):
 * the picked anchor must be SEEN, and a Burrowed anchor must be inside the caster's tremorsense with a clear line to the surface above it
 * (the FT3D scene measures that line). Anything short of a measured 'clear' — no scene, a silent one, an unmeasured line — refuses.
 */
export async function browserAreaAnchorVerdict(request: { casterId: string; encounterId: string; ability: unknown; targetId: unknown }):
    Promise<{ kind: 'refuse'; message: string } | { kind: 'post' }> {
    const ability = record(request.ability);
    const tokens = String(request.targetId ?? '').split(',').map(token => token.trim().toUpperCase()).filter(Boolean);
    if (!ability || !tokens.length || tokens.some(token => !UUID_TOKEN.test(token))) return { kind: 'post' };
    const { caster, encounter } = castRows(request.casterId, request.encounterId);
    if (!caster || !encounter) return { kind: 'post' };
    let verdict: ReturnType<typeof areaAnchorVerdict>;
    try {
        const anchors = tokens.map(id => rosterMember(encounter, id)).filter((row): row is Row => !!row);
        verdict = areaAnchorVerdict({ caster, ability, anchors, burrowed: row => positionKnown(row), tremorsenseReaches: row => tremorsenseReaches(caster, row) },
            { ...readers, visibility: (viewer, target) => abilityVisibility(encounter, viewer, target) });
    } catch (error) {
        console.error('Area anchor gate failed; the cast posts unjudged', error);
        return { kind: 'post' };
    }
    if (!verdict) return { kind: 'post' };
    if (verdict.kind === 'refuse') return verdict;
    const abilityId = upper(record(ability.items)?.item_id ?? ability.item_id);
    for (const anchor of verdict.anchors) {
        const anchorId = upper(record(anchor.encounter_creatures)?.encounter_creature_id);
        const scene = await requestSceneVerdicts({ casterId: request.casterId, encounterId: request.encounterId, abilityId, burrowedAnchorTo: anchorId });
        // Only a measured 'clear' passes: no scene, a silent scene or an unmeasured line refuses (PHP judged it or refused; Codex review).
        const line = scene.verdicts?.burrowedAnchor ?? null;
        if (line === 'blocked') return { kind: 'refuse', message: 'The line of effect to that target is blocked.' };
        if (line !== 'clear') return { kind: 'refuse', message: GATE_READ_FAILED_MESSAGE };
    }
    return { kind: 'post' };
}

/**
 * A held-item target's line of effect (cast-gate session 2026-09-29, R-103 — PHP no longer judges it for a player): the line runs to the
 * creature HOLDING the item (ft_inventory_target_holder_arr); the caster's own carried gear needs none. The FT3D scene answers through the
 * gate-query envelope; a mounted scene that does not answer refuses fail-closed (owner Q2 = A); no scene / an unheld holder posts.
 */
export async function browserHeldItemLineOfEffect(request: { casterId: string; encounterId: string; ability: unknown; targetId: unknown }):
    Promise<{ kind: 'refuse'; message: string } | { kind: 'post' }> {
    const ability = record(request.ability);
    const itemId = upper(request.targetId);
    if (!ability || !(ftLayerEntries(ability, 'ability_works_only_against_item_type')?.length) || !/^[0-9A-F-]{36}$/.test(itemId)) return { kind: 'post' };
    const encounters = window.FateTide?.encounterCache;
    const encounter = request.encounterId ? record(encounters?.get(request.encounterId) ?? encounters?.get(upper(request.encounterId))) : null;
    const casters = window.FateTide?.casterCache;
    const caster = record(casters?.get(request.casterId) ?? casters?.get(upper(request.casterId)));
    if (!encounter || carries(caster, itemId) || carries(rosterMember(encounter, request.casterId), itemId)) return { kind: 'post' };
    const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as unknown[] : [];
    const holder = roster.map(record).find(member => member && carries(member, itemId));
    const holderId = upper(record(holder?.encounter_creatures)?.encounter_creature_id);
    if (!holderId || holderId === upper(request.casterId)) return { kind: 'post' };
    const abilityId = upper(record(ability.items)?.item_id ?? ability.item_id);
    const scene = await requestSceneVerdicts({ casterId: request.casterId, encounterId: request.encounterId, abilityId, lineOfEffectTo: holderId });
    if (scene.failed) return { kind: 'refuse', message: GATE_READ_FAILED_MESSAGE };
    return scene.verdicts?.lineOfEffect === 'blocked' ? { kind: 'refuse', message: 'The line of effect to that target is blocked.' } : { kind: 'post' };
}

/** Twin / Bouncing's +1 target for the FT3D targeting profile (action-bar.ts): 0 or 1 for the cached caster, this ability and the cast's
 *  variable_1 ('spell_ritual' for an armed ritual cast — PHP gives a ritual no bonus). */
export function browserSanguineExtraTargets(casterId: string, ability: unknown, variable1: unknown = null): 0 | 1 {
    const casters = window.FateTide?.casterCache;
    const caster = record(casters?.get(casterId) ?? casters?.get(upper(casterId)));
    const row = record(ability);
    if (!caster || !row) return 0;
    try { return sanguineExtraTargets(caster, row, variable1, readers); } catch { return 0; }
}

/** D-050 (owner 2026-09-30): the range extensions of THIS cast for the FT3D targeting profile — Far Bender's upgraded range id, the Distant
 *  multiplier, and the controlled summons a cast-through-summon spell may measure (and trace its line of effect) from. null = none apply
 *  (unknown extensions refuse before targeting). */
export type CastRangeExtension =
    | { unknown: string }
    | { rangeId: string; multiplier: number; originIds: string[]; /** per origin (same order): its allowed feet on the REACH band — the
        *  measuring body's own reach × the multiplier (is_target_in_range swaps the body before reading `reach`); null = the profile's range */
        originFeet: Array<number | null> };
const REACH_RANGE_ID = '36D59FF4-6778-96ED-8664-F5D63638C77B';
export function browserCastRangeExtension(casterId: string, ability: unknown, missing?: Set<string>): CastRangeExtension | null {
    const casters = window.FateTide?.casterCache;
    const caster = record(casters?.get(casterId) ?? casters?.get(upper(casterId)));
    const row = record(ability);
    if (!caster || !row) return null;
    const read: AbilityPreconditionReaders = { ...readers, resolveItem: entry => {
        const def = resolvePageItem(entry, caster);
        if (!def) {
            const id = upper(typeof entry === 'string' ? entry : record(entry)?.item_id ?? record(record(entry)?.items)?.item_id);
            if (id) missing?.add(id);
        }
        return def;
    } };
    try {
        const authored = upper(read.value(row, 'range'));
        const rangeId = effectiveSpellRangeId(caster, row, read);
        // an extension the page cannot prove is reported, never silently replaced by the caster-only authored range (Codex D-050 review P2)
        if (rangeId === undefined) return { unknown: 'far-bender' };
        const persistent = persistentSpellRangeMultiplier(caster, row, read);
        if (persistent === undefined) return { unknown: 'persistent-range' };
        const multiplier = Math.min(10, persistent * spellRangeMultiplier(caster, row, read));
        const encounterId = upper(record(caster.encounter_creatures)?.encounter_id);
        const encounters = window.FateTide?.encounterCache;
        const encounter = encounterId ? record(encounters?.get(encounterId) ?? encounters?.get(encounterId.toLowerCase())) : null;
        const roster = Array.isArray(encounter?.encounter_creatures_arr) ? (encounter!.encounter_creatures_arr as unknown[]).map(record).filter((r): r is Record<string, unknown> => r !== null) : [];
        const summons = throughSummonOrigins(caster, row, roster, read, rangeId);
        if (summons === undefined || (summons !== null && !encounter)) return { unknown: 'cast-through-summon' };
        const originIds = (summons ?? []).map(s => upper(record(s.encounter_creatures)?.encounter_creature_id)).filter(Boolean);
        const reachOf = (r: Record<string, unknown>): number | null => { const v = readers.value(r, 'reach'); const n = v === null || v === undefined || v === '' ? 5 : Number(Array.isArray(v) ? v[0] : v); return Number.isFinite(n) ? n : null; };
        const originFeet = (summons ?? []).map(s => rangeId === REACH_RANGE_ID ? (reachOf(s) === null ? null : reachOf(s)! * multiplier) : null);
        if (rangeId === REACH_RANGE_ID && originFeet.some(f => f === null)) return { unknown: 'summon-reach' };
        if (rangeId === authored && multiplier === 1 && !originIds.length) return null;
        return { rangeId, multiplier, originIds, originFeet };
    } catch (error) { return { unknown: 'error:' + String(error instanceof Error ? error.message : error).slice(0, 60) }; }
}

/** Read only definitions the range walk actually missed. The deadline covers adoption as well
 * as transport; a parked compile must not leave the action waiting forever. */
export async function ensureCastRangeExtension(casterId: string, ability: unknown): Promise<CastRangeExtension | null> {
    const fetchBatch = (window as Window & { ftFetchObjectsBatch?: (ids: string[]) => Promise<void> }).ftFetchObjectsBatch;
    if (!fetchBatch) return browserCastRangeExtension(casterId, ability);
    let expired = false;
    let timer: number | undefined;
    const deadline = new Promise<CastRangeExtension>(resolve => {
        timer = window.setTimeout(() => { expired = true; resolve({ unknown: 'range-read-timeout' }); }, 3000);
    });
    const gather = async (): Promise<CastRangeExtension | null> => {
        const attempted = new Set<string>();
        while (!expired) {
            const missing = new Set<string>();
            const verdict = browserCastRangeExtension(casterId, ability, missing);
            const ids = [...missing].filter(id => !attempted.has(id));
            if (!verdict || !('unknown' in verdict) || !ids.length) return verdict;
            ids.forEach(id => attempted.add(id));
            try { await fetchBatch(ids); }
            catch { return { unknown: 'range-read-failed' }; }
        }
        return { unknown: 'range-read-timeout' };
    };
    try { return await Promise.race([gather(), deadline]); }
    finally { window.clearTimeout(timer); }
}

// Other bundles reach the same page gates through these bridges.
Object.assign(window, { ftAbilityPreconditionRefusal: browserAbilityPreconditionRefusal, ftAbilityGateVerdict: browserAbilityGateVerdict, ftEnsureAbilityGateData: ensureAbilityGateData,
    ftSanguineExtraTargets: browserSanguineExtraTargets, ftSelectionVerdict: browserSelectionVerdict, ftTargetRuleRefusal: browserTargetRuleRefusal,
    ftCastRangeExtension: browserCastRangeExtension, ftEnsureCastRangeExtension: ensureCastRangeExtension, ftCastGrantVerdict: browserCastGrantVerdict });
Object.assign(window, { ftResolveAbilityDefinition: readers.resolveItem });
